import { Component, Input, OnChanges, OnDestroy, OnInit, SimpleChanges } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { Subject } from 'rxjs';
import { filter, takeUntil } from 'rxjs/operators';
import { Menu } from '../menu/models/menu.model';
import { MenuBadgeService } from '../menu/services/menu-badge.service';
import { MenuService } from '../menu/services/menu.service';
import { UserAuthService } from '../../../shared/services/user-auth.service';
import {LoginService} from '../../services/login.service';
import { environment } from '../../../../environments/environment';
import { AD_RESTRICTED_ROLES, RolesEnum } from '../../../shared/enum/role.enum';

interface MobileNavItem extends Menu {
  badge?: 'chat' | 'activities' | 'survey';
}

@Component({
  selector: 'app-mobile-bottom-nav',
  templateUrl: './mobile-bottom-nav.component.html',
  styleUrls: ['./mobile-bottom-nav.component.scss'],
  standalone: false,
})
export class MobileBottomNavComponent implements OnInit, OnChanges, OnDestroy {
  @Input() menuItems: Menu[] = [];

  primaryItems: MobileNavItem[] = [];
  overflowItems: MobileNavItem[] = [];
  isOverflowActive = false;
  showMoreBadge = false;

  readonly unreadMessages$ = this.menuBadgeService.unreadMessages$;
  /** One dot for the Activities entry: POS Integrity scans or unsubmitted activities. */
  readonly activitiesNavBadge$ = this.menuBadgeService.activitiesNavBadge$;
  readonly requestedSurveysBadge$ = this.menuBadgeService.requestedSurveysBadge$;

  private readonly onDestroy = new Subject<void>();
  private readonly visibleItemsWithMore = 4;
  private readonly maxVisibleItems = 5;
  private allItems: MobileNavItem[] = [];
  private hasUnreadMessages = false;
  private hasActivitiesWork = false;
  private hasRequestedSurveys = false;
  private promotedRoute: string | null = null;

  constructor(
    private readonly router: Router,
    private readonly menuBadgeService: MenuBadgeService,
    public readonly menuService: MenuService,
    private readonly userAuthService: UserAuthService,
    private readonly loginSvc: LoginService
  ) {}

  ngOnInit(): void {
    this.rebuildItems();
    this.unreadMessages$
      .pipe(takeUntil(this.onDestroy))
      .subscribe(unreadMessages => {
        this.hasUnreadMessages = unreadMessages;
        this.updateMoreBadge();
      });
    this.activitiesNavBadge$
      .pipe(takeUntil(this.onDestroy))
      .subscribe(hasWork => {
        this.hasActivitiesWork = hasWork;
        this.updateMoreBadge();
      });
    this.requestedSurveysBadge$
      .pipe(takeUntil(this.onDestroy))
      .subscribe(requestedSurveys => {
        this.hasRequestedSurveys = requestedSurveys;
        this.updateMoreBadge();
      });
    this.router.events
      .pipe(
        filter(event => event instanceof NavigationEnd),
        takeUntil(this.onDestroy)
      )
      .subscribe(() => {
        this.promoteCurrentOverflowRoute();
        this.applyItemLayout();
      });
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['menuItems']) {
      this.rebuildItems();
    }
  }

  ngOnDestroy(): void {
    this.onDestroy.next();
    this.onDestroy.complete();
  }

  get showFieldManagementReporting(): boolean {
    return !this.loginSvc.isUkUser()
      && !AD_RESTRICTED_ROLES.includes(this.loginSvc._role as RolesEnum);
  }

  goToPowerBI(): void {
    globalThis.open(environment.powerBiUrl);
  }

  promoteItem(item: MobileNavItem): void {
    this.promotedRoute = this.normalizeRoute(item.routerLink);
    this.applyItemLayout();
  }

  onOverflowItemClick(item: MobileNavItem): void {
    this.menuService.handleMenuClick(item);
    if (!this.menuService.getRouterLink(item)) {
      return;
    }

    this.promoteItem(item);
  }

  private rebuildItems(): void {
    this.allItems = (this.menuItems ?? [])
      .filter(item => this.hasRoleAccess(item))
      .filter(item => item.parentId === 0)
      .map(item => ({
        ...item,
        routerLink: this.normalizeRoute(item.routerLink || item.href),
        title: this.getMobileTitle(item),
        badge: this.getBadge(item),
      }));

    this.promoteCurrentOverflowRoute();
    this.applyItemLayout();
  }

  private applyItemLayout(): void {
    const allowedItems = this.allItems;
    const visibleItemCount = allowedItems.length > this.maxVisibleItems
      ? this.visibleItemsWithMore
      : this.maxVisibleItems;

    const primaryItems = allowedItems.slice(0, visibleItemCount);
    let overflowItems = allowedItems.slice(visibleItemCount);

    if (allowedItems.length > this.maxVisibleItems && this.promotedRoute) {
      const promotedItem = overflowItems.find(item => this.normalizeRoute(item.routerLink) === this.promotedRoute);

      if (promotedItem) {
        const promotedSlotIndex = this.visibleItemsWithMore - 1;
        const displacedItem = primaryItems[promotedSlotIndex];

        if (displacedItem) {
          primaryItems[promotedSlotIndex] = promotedItem;
          overflowItems = [
            displacedItem,
            ...overflowItems.filter(item => this.normalizeRoute(item.routerLink) !== this.promotedRoute),
          ];
        }
      } else {
        this.promotedRoute = null;
      }
    }

    this.primaryItems = primaryItems;
    this.overflowItems = overflowItems;

    this.updateOverflowActive();
    this.updateMoreBadge();
  }

  private promoteCurrentOverflowRoute(): void {
    const baseOverflowItems = this.allItems.length > this.maxVisibleItems
      ? this.allItems.slice(this.visibleItemsWithMore)
      : [];

    const currentItem = baseOverflowItems.find(item => this.isCurrentRoute(item));
    if (currentItem) {
      this.promotedRoute = this.normalizeRoute(currentItem.routerLink);
    }
  }

  private getMobileTitle(item: Menu): string {
    let isUkUser = this.loginSvc.isUkUser();
    switch (this.normalizeRoute(item.routerLink)) {
      case '/serviced':
        return 'Serviced';
      case '/non-serviced':
        return 'Non Serv';
      case '/service-records':
        return 'Records';
      case '/schedule-appointment':
        return 'Schedule';
      case '/territory-changes':
        return 'Terr Chg'
      case '/user-roles':
        return 'Override';
      case '/associates':
        return isUkUser ? 'Merch' : item.title;
      default:
        return item.title;
    }
  }

  private getBadge(item: Menu): MobileNavItem['badge'] {
    if (this.normalizeRoute(item.routerLink) === '/chat' || item.icon === 'forum') {
      return 'chat';
    }

    if (this.normalizeRoute(item.routerLink) === '/activities' || item.icon === 'assignment_turned_in') {
      return 'activities';
    }

    if (this.normalizeRoute(item.routerLink) === '/store-survey' || item.icon === 'assignment') {
      return 'survey';
    }

    return undefined;
  }

  hasOverflowBadge(badge: MobileNavItem['badge']): boolean {
    return this.overflowItems.some(item => item.badge === badge);
  }

  private updateMoreBadge(): void {
    this.showMoreBadge =
      (this.hasOverflowBadge('chat') && this.hasUnreadMessages) ||
      (this.hasOverflowBadge('activities') && this.hasActivitiesWork) ||
      (this.hasOverflowBadge('survey') && this.hasRequestedSurveys);
  }

  private hasRoleAccess(item: Menu): boolean {
    const excludedRoles = item.excludedRoles ?? [];

    if (!this.userAuthService.isAuthenticated()) {
      return false;
    }

    return !excludedRoles.some(role => this.userAuthService.getRoles().includes(role));
  }

  private updateOverflowActive(): void {
    this.isOverflowActive = this.overflowItems.some(item => this.isCurrentRoute(item));
  }

  /** Mirrors the nav links' `routerLinkActive`: prefix match for items whose page uses child routes. */
  private isCurrentRoute(item: Menu): boolean {
    const route = this.normalizeRoute(item.routerLink);
    const currentRoute = this.getCurrentRoute();
    return item.exactRouteMatch === false
      ? currentRoute === route || currentRoute.startsWith(`${route}/`)
      : currentRoute === route;
  }

  private getCurrentRoute(): string {
    return this.router.url.split('?')[0].split('#')[0];
  }

  private normalizeRoute(route: string): string {
    if (!route) {
      return route;
    }

    return route.startsWith('/') ? route : `/${route}`;
  }
}
